// signals-web.ts — 1:1 JS port of src-tauri/src/signals.rs: the derived SeasonSignals the
// season recap narrates. 🔴 WHY: compute_signals was a zero stub in web-commands, so "recap my
// season" answered "Log a few climbs first — there's nothing to recap yet" WITH CLIMBS ON
// SCREEN (measured live 2026-08-19). Where this and the Rust could drift, the Rust wins.
//
// One deliberate divergence: grade ordinals are recomputed here from the raw grade STRING
// (grade-web.ts) instead of trusting the stored grade_value — web rows written before
// 2026-08-19 carry -1 (the create stub never computed it), and recomputing heals them.

import { gradeValue } from "./grade-web";

export interface WebClimb {
  climbed_on: string;
  grade: string;
  grade_value: number;
  environment: string;
  venue: string;
  discipline: string;
  style: string[];
  ascent: string;
  attempts: number;
  sent: boolean;
  project: boolean;
  partner: string;
}

const SEASON_DAYS = 365;
const round2 = (x: number) => Math.round(x * 100) / 100;
const parseDay = (d: string) => (/^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10) : null);
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);
const addDaysIso = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);

function medianI(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor((s.length - 1) / 2)]; // lower-middle for even counts — same as Rust
}

function topCounts(map: Map<string, number>, cap: number) {
  return [...map.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
    .slice(0, cap);
}

export function computeSignalsWeb(all: WebClimb[], today: string) {
  const gv = (c: WebClimb) => (c.grade_value >= 0 ? c.grade_value : gradeValue(c.grade ?? ""));
  const dated = all.filter((c) => parseDay(c.climbed_on ?? ""));

  // ── Overall (whole corpus) ──
  const totalClimbs = dated.length;
  const totalSends = dated.filter((c) => c.sent).length;
  const dayList = dated.map((c) => c.climbed_on.slice(0, 10)).sort();
  const first = dayList[0] ?? null;
  const last = dayList[dayList.length - 1] ?? null;
  const overall = {
    total_climbs: totalClimbs,
    total_sends: totalSends,
    total_attempts_only: totalClimbs - totalSends,
    distinct_days: new Set(dayList).size,
    days_span: first && last ? dayDiff(first, last) + 1 : 0,
    first_date: first,
    last_date: last,
    note: "Whole logged history. Counts only — describe what was logged, never advise.",
  };

  // ── Season window: trailing 365 days ──
  const seasonStart = addDaysIso(today, -(SEASON_DAYS - 1));
  const inSeason = dated.filter((c) => { const d = c.climbed_on.slice(0, 10); return d >= seasonStart && d <= today; });
  const seasonSends = inSeason.filter((c) => c.sent);
  const season = {
    label: "last 365 days",
    start: seasonStart,
    end: today,
    climbs: inSeason.length,
    sends: seasonSends.length,
    attempts_only: inSeason.length - seasonSends.length,
    distinct_days: new Set(inSeason.map((c) => c.climbed_on.slice(0, 10))).size,
    distinct_venues: new Set(inSeason.filter((c) => c.venue).map((c) => c.venue.toLowerCase())).size,
  };

  // ── Hardest send this season (grade ordinal, ties → recency) ──
  let hardest: Record<string, unknown> | null = null;
  const gradedSends = seasonSends.filter((c) => gv(c) >= 0);
  if (gradedSends.length) {
    const best = gradedSends.reduce((a, b) =>
      gv(b) > gv(a) || (gv(b) === gv(a) && b.climbed_on > a.climbed_on) ? b : a);
    const val = gv(best);
    hardest = {
      grade: best.grade, climbed_on: best.climbed_on, venue: best.venue,
      discipline: best.discipline, environment: best.environment, ascent: best.ascent,
      ties_at_or_above: gradedSends.filter((x) => gv(x) >= val).length,
    };
  }

  // ── Attempts-before-tick ──
  const worked: number[] = [];
  let flashed = 0;
  let maxAtt: number | null = null;
  for (const c of seasonSends) {
    maxAtt = maxAtt == null ? c.attempts : Math.max(maxAtt, c.attempts);
    if (c.ascent === "flash" || c.ascent === "onsight" || c.attempts <= 1) flashed++;
    else worked.push(c.attempts);
  }
  const attempts_before_tick = {
    worked_sends: worked.length,
    flashed_or_onsight: flashed,
    // same odd-looking rounding as the Rust (round2 of ×10 then ÷10 → one decimal place)
    mean_attempts_worked: worked.length ? round2((worked.reduce((s, x) => s + x, 0) / worked.length) * 10) / 10 : null,
    max_attempts: maxAtt,
    note: "Attempts are sessions/tries the climber logged. Describe the number; never call it many/few or advise.",
  };

  // ── Grade drift: first vs second half of the season ──
  const mid = addDaysIso(seasonStart, Math.floor(SEASON_DAYS / 2));
  const firstVals: number[] = [];
  const secondVals: number[] = [];
  const labelForVal = new Map<number, string>();
  for (const c of seasonSends) {
    const v = gv(c);
    if (v < 0) continue;
    if (!labelForVal.has(v)) labelForVal.set(v, c.grade);
    if (c.climbed_on.slice(0, 10) < mid) firstVals.push(v); else secondVals.push(v);
  }
  let grade_drift: Record<string, unknown> | null = null;
  if (firstVals.length >= 2 && secondVals.length >= 2) {
    const fMed = medianI(firstVals)!;
    const sMed = medianI(secondVals)!;
    const delta = sMed - fMed;
    grade_drift = {
      first_half_median_value: fMed, first_half_label: labelForVal.get(fMed) ?? "", first_half_sends: firstVals.length,
      second_half_median_value: sMed, second_half_label: labelForVal.get(sMed) ?? "", second_half_sends: secondVals.length,
      delta_value: delta, direction: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
    };
  }

  // ── Indoor/outdoor split ──
  const indoor = inSeason.filter((c) => c.environment === "indoor").length;
  const outdoor = inSeason.filter((c) => c.environment === "outdoor").length;
  const environment_split = {
    indoor, outdoor,
    indoor_fraction: indoor + outdoor > 0 ? round2(indoor / (indoor + outdoor)) : null,
  };

  // ── Mixes ──
  const bump = (m: Map<string, number>, k: string) => m.set(k, (m.get(k) ?? 0) + 1);
  const ascentMap = new Map<string, number>();
  const disciplineMap = new Map<string, number>();
  const styleMap = new Map<string, number>();
  const venueMap = new Map<string, number>();
  const partnerMap = new Map<string, number>();
  for (const c of inSeason) {
    bump(ascentMap, c.ascent);
    bump(disciplineMap, c.discipline);
    for (const s of c.style ?? []) if (s) bump(styleMap, s);
    if (c.venue) bump(venueMap, c.venue);
    if (c.partner) bump(partnerMap, c.partner);
  }

  // ── Projects (grade+venue+discipline lines, most-worked first) ──
  const projMap = new Map<string, { grade: string; venue: string; discipline: string; total_attempts: number; sessions: number; sent: boolean; last_touched: string }>();
  for (const c of inSeason) {
    if (!c.project) continue;
    const key = `${c.grade} ${c.venue} ${c.discipline}`;
    const e = projMap.get(key) ?? { grade: c.grade, venue: c.venue, discipline: c.discipline, total_attempts: 0, sessions: 0, sent: false, last_touched: "" };
    e.total_attempts += c.attempts;
    e.sessions += 1;
    e.sent = e.sent || c.sent;
    if (c.climbed_on > e.last_touched) e.last_touched = c.climbed_on;
    projMap.set(key, e);
  }
  const projects = [...projMap.values()]
    .sort((a, b) => b.total_attempts - a.total_attempts || b.sessions - a.sessions || (b.last_touched < a.last_touched ? -1 : 1))
    .slice(0, 8);

  return {
    schema:
      "oriora.send_log.v1-signals: derived aggregates from a climbing tick list (no raw notes). grade_value fields are internal ordinals (higher=harder) used only for ordering/drift — ALWAYS quote the raw grade strings, never the ordinals, to the user. Every figure describes PAST logged climbs only. STRICT FENCE: observational/CAN-only — describe counts, splits, medians, drift; NEVER advise a grade to try, NEVER give training/technique/finger-injury or any coaching/medical guidance, NEVER label a grade easy/hard or judge the climber. Narrate the season they logged.",
    generated_for: today,
    overall,
    season,
    hardest,
    attempts_before_tick,
    grade_drift,
    environment_split,
    ascent_mix: topCounts(ascentMap, 6),
    discipline_mix: topCounts(disciplineMap, 6),
    style_mix: topCounts(styleMap, 10),
    top_venues: topCounts(venueMap, 6),
    top_partners: topCounts(partnerMap, 6),
    projects,
  };
}
